import { Alert, Select, Text } from '@mantine/core';
import type { ComboboxItem } from '@mantine/core';
import type { AdPlatform, Line, Meta } from '@marketing-tool/shared';
import { useMemo } from 'react';

import { useCampaigns } from '../../data/game-report';
import type { Campaign } from '../../data/game-report/types';
import * as remote from '../../data/remote-data';
import { formatDeliveryStatus } from '../../lib/status';
import type { Status } from '../../lib/status';

const groupByDeliveryStatus = <
    T extends { id: string | number; name: string } & (
        | { deliveryStatus: Line.DeliveryStatus }
        | { effectiveStatus: Meta.EffectiveStatus }
    ),
>(
    items: T[],
): { group: string; items: ComboboxItem[] }[] => {
    const targets: {
        [key in Status]?: ComboboxItem[];
    } = {};

    for (const item of items) {
        const status = 'deliveryStatus' in item ? item.deliveryStatus : item.effectiveStatus;
        targets[status] ??= [];
        const target = targets[status];
        target.push({ value: item.id.toString(), label: item.name });
    }

    const groups: { group: string; items: ComboboxItem[] }[] = [];
    Object.entries(targets).forEach(([deliveryStatus, items]) => {
        if (items.length) {
            groups.push({
                group: formatDeliveryStatus(deliveryStatus as keyof typeof targets),
                items,
            });
        }
    });
    return groups;
};

export const CampaignSelector = ({
    id,
    gameId,
    platform,
    value,
    onChange,
    label,
}: {
    id: string;
    gameId: string;
    platform: AdPlatform;
    value?: string | number;
    onChange: (campaign: Campaign) => void;
    label?: string;
}) => {
    const campaigns = useCampaigns(gameId, platform);
    const groups = useMemo(() => remote.map(campaigns.data, groupByDeliveryStatus), [campaigns.data]);
    return remote.matchAll(groups, {
        NotAsked: () => null,
        Loading: () => (
            <Text c="dimmed" fs="italic">
                Loading campaigns...
            </Text>
        ),
        Failure: (error) => (
            <Alert color="red" title="Error loading campaigns">
                {error}
            </Alert>
        ),
        Success: (groups) => (
            <Select
                id={id}
                label={label}
                value={value?.toString() ?? null}
                onChange={(id) => {
                    const campaign = remote
                        .withDefault(campaigns.data, [])
                        .find((campaign) => campaign.id.toString() === id);
                    campaign && onChange(campaign);
                }}
                data={groups}
                placeholder="Select a campaign"
            />
        ),
    });
};
